import walkthroughPoster from '@/assets/videos/evidence/walkthrough-poster.webp';
import {
  evidencePostUpgradeOnboardingModalOpenAtom,
  evidencePostUpgradeWelcomeDeferQueueUntilAfterOnboardingAtom,
} from '@/atoms/evidencePostUpgradeOnboardingAtoms';
import { seenModalIdsAtom } from '@/atoms/modalQueueAtoms';
import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { cn } from '@/lib/cn';
import { trackEvidencePostUpgradeWelcomeClick } from '@/models/analytics/features/trackEvidencePostUpgradeAnalytics';
import {
  EVIDENCE_POST_UPGRADE_WELCOME_ACTION,
  TRIALS_AND_UPGRADES_SCREEN,
  type EvidencePostUpgradeWelcomeActionToken,
} from '@heidi/commerce/contracts';
import { Button } from '@heidi/design-system/button';
import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { ModalId, ModalViewAction, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { X } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { evidencePostUpgradeWelcomeMessages } from '@heidi/commerce';
import { useUserTier } from '@heidi/commerce/data';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
const MODAL_ID = ModalId.EVIDENCE_POST_UPGRADE_WELCOME_POPUP;

/**
 * Floating welcome card after the org moves to paid Clinician / Practice Evidence when
 * the clinician trial has ended with a PRO / PRO_PLUS baseline (checkout or team upgrade).
 */
export const EvidencePostUpgradeWelcomePopup = () => {
  const intl = useIntl();
  const { userTier } = useUserTier();
  const { isOpen, handleClose } = useQueuedModal(MODAL_ID);
  const { mutate: markModalAsViewedBackend } = useMutateMarkModalAsViewed();
  const setSeenModalIds = useSetAtom(seenModalIdsAtom);
  const setEvidencePostUpgradeOnboardingOpen = useSetAtom(
    evidencePostUpgradeOnboardingModalOpenAtom
  );
  const setDeferWelcomeQueueUntilAfterOnboarding = useSetAtom(
    evidencePostUpgradeWelcomeDeferQueueUntilAfterOnboardingAtom
  );
  const deferWelcomeQueueUntilAfterOnboarding = useAtomValue(
    evidencePostUpgradeWelcomeDeferQueueUntilAfterOnboardingAtom
  );
  const evidencePostUpgradeOnboardingOpen = useAtomValue(
    evidencePostUpgradeOnboardingModalOpenAtom
  );
  const hasDismissedRef = useRef(false);
  const prevOnboardingOpenRef = useRef(evidencePostUpgradeOnboardingOpen);

  useEffect(() => {
    if (isOpen) {
      hasDismissedRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    const wasOnboardingOpen = prevOnboardingOpenRef.current;
    prevOnboardingOpenRef.current = evidencePostUpgradeOnboardingOpen;

    if (
      !wasOnboardingOpen ||
      evidencePostUpgradeOnboardingOpen ||
      !deferWelcomeQueueUntilAfterOnboarding
    ) {
      return;
    }

    setSeenModalIds((prev) => new Set([...(prev ?? []), MODAL_ID]));
    markModalAsViewedBackend({
      modal_id: MODAL_ID,
      increment_count: true,
      action: ModalViewAction.engage,
    });
    setDeferWelcomeQueueUntilAfterOnboarding(false);
  }, [
    deferWelcomeQueueUntilAfterOnboarding,
    evidencePostUpgradeOnboardingOpen,
    markModalAsViewedBackend,
    setDeferWelcomeQueueUntilAfterOnboarding,
    setSeenModalIds,
  ]);

  const isClinicianPlan = userTier === SubscriptionTier.CLINICIAN_PRO;

  const trackButton = useCallback(
    (action: EvidencePostUpgradeWelcomeActionToken, label: string) => {
      trackEvidencePostUpgradeWelcomeClick({
        screen_name: TRIALS_AND_UPGRADES_SCREEN.evidencePostUpgradeWelcome,
        action,
        label,
        ...(userTier !== undefined && { current_plan: userTier }),
      });
    },
    [userTier]
  );

  const dismiss = useCallback(() => {
    if (hasDismissedRef.current) return;
    hasDismissedRef.current = true;
    handleClose();
  }, [handleClose]);

  const handleCloseClick = useCallback(() => {
    trackButton(
      EVIDENCE_POST_UPGRADE_WELCOME_ACTION.close,
      intl.formatMessage(evidencePostUpgradeWelcomeMessages.close)
    );
    dismiss();
  }, [dismiss, intl, trackButton]);

  const handleSeeWhatsNew = useCallback(() => {
    trackButton(
      EVIDENCE_POST_UPGRADE_WELCOME_ACTION.seeWhatsNew,
      intl.formatMessage(evidencePostUpgradeWelcomeMessages.seeWhatsNew)
    );
    setDeferWelcomeQueueUntilAfterOnboarding(true);
    setEvidencePostUpgradeOnboardingOpen(true);
  }, [
    intl,
    setDeferWelcomeQueueUntilAfterOnboarding,
    setEvidencePostUpgradeOnboardingOpen,
    trackButton,
  ]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        if (deferWelcomeQueueUntilAfterOnboarding) {
          return;
        }
        dismiss();
      }
    },
    [deferWelcomeQueueUntilAfterOnboarding, dismiss]
  );

  const titleMessage = isClinicianPlan
    ? evidencePostUpgradeWelcomeMessages.titleClinician
    : evidencePostUpgradeWelcomeMessages.titlePractice;

  const TierIcon = isClinicianPlan ? TierProFilled : TierPracticeFilled;

  return (
    <Popover
      open={isOpen && !deferWelcomeQueueUntilAfterOnboarding}
      onOpenChange={handleOpenChange}
      modal={false}
    >
      <PopoverAnchor
        aria-hidden
        className="pointer-events-none fixed bottom-4 start-4 z-[55] size-px"
      />
      <PopoverContent
        side="top"
        align="start"
        sideOffset={0}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        aria-label={intl.formatMessage(evidencePostUpgradeWelcomeMessages.ariaLabel)}
        data-testid="evidence-post-upgrade-welcome-popup"
        className={cn(
          'max-w-[240px] overflow-hidden rounded-xl border-0 bg-surface-container p-0 shadow-sheet',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
        )}
      >
        <div className="flex flex-col">
          <div className="relative aspect-[240/135] w-full overflow-hidden">
            <video
              src="/videos/evidence/walkthrough.mp4"
              poster={walkthroughPoster}
              autoPlay
              muted
              loop
              playsInline
              aria-label={intl.formatMessage(
                evidencePostUpgradeWelcomeMessages.videoTitle
              )}
              className="absolute inset-0 size-full object-cover"
              data-testid="evidence-walkthrough-preview"
            />
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-end p-2">
              <Button
                type="button"
                variant="ghost"
                size="xxs"
                iconOnly
                aria-label={intl.formatMessage(evidencePostUpgradeWelcomeMessages.close)}
                onClick={handleCloseClick}
                className="pointer-events-auto shrink-0 bg-surface/50 text-text-primary hover:bg-surface/80"
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-4 p-4">
            <div className="flex flex-col gap-1">
              <TypographyP2SemiBold className="text-text-primary">
                <FormattedMessage {...titleMessage} />
              </TypographyP2SemiBold>
              <TypographyCaption className="text-text-secondary">
                <FormattedMessage {...evidencePostUpgradeWelcomeMessages.subtitle} />
              </TypographyCaption>
            </div>

            <Button
              type="button"
              variant={isClinicianPlan ? 'pro' : 'pro_plus'}
              size="xs"
              onClick={handleSeeWhatsNew}
              className="w-full"
            >
              <TierIcon size={16} />
              <FormattedMessage {...evidencePostUpgradeWelcomeMessages.seeWhatsNew} />
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
